import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Watermark from '../components/Watermark';
import SubtaskList from '../components/SubtaskList';
import CommentsPanel from '../components/CommentsPanel';
import AttachmentSection from '../components/AttachmentSection';
import { toast } from 'react-hot-toast';
import {
  isTaskReadOnly,
  canCreateSubtask,
  canSubmitReport,
  canChangeStatus,
  canCloseTask,
  supportsSubtasks,
} from '../utils/taskRules';
import { getMinDate, validateDueDate } from '../utils/dateUtils';
import * as XLSX from 'xlsx';
import { buildSpecificTaskWorkbook } from '../utils/exportUtils';

const PRIORITY_BADGE = {
  High:   'bg-red-100 text-red-700',
  Medium: 'bg-yellow-100 text-yellow-700',
  Low:    'bg-green-100 text-green-700',
};
const STATUS_BADGE = {
  open:        'bg-blue-100 text-blue-700',
  in_progress: 'bg-yellow-100 text-yellow-700',
  blocked:     'bg-red-100 text-red-700',
  on_hold:     'bg-orange-100 text-orange-700',
  closed:      'bg-gray-100 text-gray-500',
};

export default function TaskDetails() {
  const { taskId } = useParams();
  const navigate   = useNavigate();
  const apiUrl     = process.env.REACT_APP_API_URL;
  const token      = localStorage.getItem('jwt') || localStorage.getItem('token');

  const decoded = (() => {
    try { return JSON.parse(atob(token.split('.')[1])); } catch { return {}; }
  })();
  const currentUserId = decoded.userId;
  const role          = decoded.role;
  // isManager = true for Manager, admin, superuser — used for UI management panel visibility.
  // For PROJECT tasks, admin/superuser will be blocked by the backend anyway.
  const isManager     = role === 'Manager' || role === 'admin' || role === 'superuser';

  const currentUser = { userId: currentUserId, role };

  const [task, setTask]             = useState(null);
  const [isPeerAccess, setIsPeerAccess] = useState(false);
  const [loading, setLoading]       = useState(true);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [unreadCount, setUnreadCount]   = useState(0);
  const [reportNotes, setReportNotes]   = useState('');
  const [reportSending, setReportSending] = useState(false);
  const [reportMsg, setReportMsg]       = useState('');
  const [statusUpdating, setStatusUpdating] = useState(false);

  // ── Manager / Admin management state ──────────────────────────────────────
  // Edit task details
  const [editMode, setEditMode]             = useState(false);
  const [editTitle, setEditTitle]           = useState('');
  const [editDesc, setEditDesc]             = useState('');
  const [editSaving, setEditSaving]         = useState(false);
  // Priority + due date
  const [editPriority, setEditPriority]     = useState('');
  const [editDueDate, setEditDueDate]       = useState('');
  const [editPrioritySaving, setEditPrioritySaving] = useState(false);
  // Assign
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [assignSaving, setAssignSaving]       = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [assigneeSearch, setAssigneeSearch]   = useState('');
  // Close task
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closureFeedback, setClosureFeedback] = useState('');
  const [closing, setClosing]               = useState(false);
  // ── Export state ────────────────────────────────────────────────────────────
  const [exporting, setExporting]           = useState(false);
  // Reports viewer state removed — Employee Reports panel has been removed from UI
  // Reports are delivered via email only

  useEffect(() => {
    fetchTask();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  const fetchTask = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const tasksRes = await axios.get(`${apiUrl}/tasks`, { headers });
      let found = (tasksRes.data || []).find(t => String(t.id) === String(taskId));
      let peerAccess = false;

      if (!found) {
        try {
          const peerRes = await axios.get(`${apiUrl}/tasks/project-peers`, { headers });
          const peerMatch = (peerRes.data || []).find(t => String(t.id) === String(taskId));
          if (peerMatch) { found = peerMatch; peerAccess = true; }
        } catch { /* ignore */ }
      }

      if (!found) {
        try {
          const subRes = await axios.get(`${apiUrl}/subtasks/assigned/me`, { headers });
          const match  = (subRes.data || []).find(s => String(s.task_id) === String(taskId));
          if (match) {
            found = {
              id:               Number(taskId),
              title:            match.parent_task_title,
              description:      null,
              status:           match.parent_task_status,
              priority:         match.parent_task_priority,
              assignee_name:    match.parent_assignee_name || null,
              created_by_name:  match.created_by_name     || null,
              due_date:         null,
              created_at:       null,
              assigned_to:      null,
              _subtaskAccessOnly: true,
            };
          }
        } catch { /* ignore */ }
      }

      // Fallback for employees viewing their own ADMIN_REQUEST (created_by = userId,
      // but not assigned_to userId so it doesn't appear in GET /tasks)
      if (!found) {
        try {
          const reqRes = await axios.get(`${apiUrl}/tasks/my-requests`, { headers });
          const reqMatch = (reqRes.data || []).find(t => String(t.id) === String(taskId));
          if (reqMatch) { found = reqMatch; }
        } catch { /* ignore */ }
      }

      if (!found) { toast.error('Task not found or access denied'); navigate(-1); return; }
      setTask(found);
      setIsPeerAccess(peerAccess);

      if (!found._subtaskAccessOnly) {
        try {
          const r = await axios.get(`${apiUrl}/tasks/${taskId}/comments/unread-count`, { headers });
          setUnreadCount(r.data?.unread || 0);
        } catch { /* ignore */ }
      }
    } catch {
      toast.error('Error loading task');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    if (newStatus === 'Closed' && !closeAllowed) {
      toast.error(
        isAdminTask || isAdminRequest
          ? 'Only admin can close this task'
          : 'Only your manager can close this task'
      );
      return;
    }
    setStatusUpdating(true);
    try {
      await axios.put(`${apiUrl}/tasks/${taskId}/status`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTask(t => ({ ...t, status: newStatus }));
      toast.success('Status updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error updating status');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleSendReport = async (e) => {
    e.preventDefault();
    if (!reportNotes.trim()) return;
    setReportSending(true);
    setReportMsg('');
    try {
      await axios.post(`${apiUrl}/tasks/${taskId}/report`, { notes: reportNotes }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReportMsg('success');
      setReportNotes('');
      toast.success('Report sent to your manager');
    } catch (err) {
      const msg = err.response?.data?.error || 'Error sending report';
      setReportMsg(msg);
      toast.error(msg);
    } finally {
      setReportSending(false);
    }
  };

  // ── Load assignable users (project members for managers; all non-superusers for admin) ──
  const loadAssignableUsers = useCallback(async () => {
    if (!isManager || assignableUsers.length > 0) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const isAdmin = role === 'admin' || role === 'superuser';
      if (isAdmin) {
        // Admins can assign to anyone except superusers (role_id 3)
        // Also exclude the currently assigned person so you can't "reassign" to the same user
        const res = await axios.get(`${apiUrl}/users`, { headers });
        const all = res.data || [];
        setAssignableUsers(
          all.filter(u => Number(u.role_id) !== 3 && Number(u.id) !== Number(task?.assigned_to))
        );
      } else {
        // Manager: restrict to project members who are employees, excluding current assignee
        const res = await axios.get(`${apiUrl}/tasks/${taskId}/comments/members`, { headers });
        const all = res.data || [];
        setAssignableUsers(
          all.filter(u => {
            const roleName = (u.role || u.role_name || '').toLowerCase();
            const isEmployee = roleName === 'employee' || (!roleName && u.role_id === 1);
            const isCurrentAssignee = Number(u.id) === Number(task?.assigned_to);
            return isEmployee && !isCurrentAssignee;
          })
        );
      }
    } catch { /* non-fatal */ }
  }, [isManager, role, assignableUsers.length, apiUrl, token, taskId, task?.assigned_to]);

  // ── Save edited title + description ───────────────────────────────────────
  const handleSaveEdit = async () => {
    if (!editTitle.trim()) return;
    setEditSaving(true);
    try {
      await axios.put(`${apiUrl}/tasks/${taskId}/admin-update`,
        { title: editTitle.trim(), description: editDesc.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTask(t => ({ ...t, title: editTitle.trim(), description: editDesc.trim() }));
      setEditMode(false);
      toast.success('Task updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error updating task');
    } finally { setEditSaving(false); }
  };

  // ── Save priority + due date ───────────────────────────────────────────────
  const handleSavePriority = async () => {
    // If the task has no priority set and the user hasn't selected one,
    // prompt them to pick a priority before saving.
    const priorityToSave = editPriority || task.priority;
    if (!priorityToSave) {
      toast.error('Please select a priority before saving.');
      return;
    }
    const dueDateToSave  = editDueDate !== '' ? editDueDate : (task.due_date ? task.due_date.split('T')[0] : null);
    setEditPrioritySaving(true);
    try {
      await axios.put(`${apiUrl}/tasks/${taskId}/admin-update`,
        { priority: priorityToSave, due_date: dueDateToSave || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Reflect changes in the header card immediately
      setTask(t => ({ ...t, priority: priorityToSave, due_date: dueDateToSave || null }));
      // Reset edit state so priority buttons reflect the newly saved value
      setEditPriority('');
      setEditDueDate('');
      toast.success('Priority & due date updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error updating priority');
    } finally { setEditPrioritySaving(false); }
  };

  // ── Reassign task ─────────────────────────────────────────────────────────
  const handleAssign = async () => {
    if (!selectedAssignee) return;
    setAssignSaving(true);
    try {
      await axios.put(`${apiUrl}/tasks/${taskId}/assign`,
        { assigned_to: Number(selectedAssignee) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const u = assignableUsers.find(m => String(m.id) === String(selectedAssignee));
      const name = u ? `${u.first_name} ${u.last_name}`.trim() : '';
      setTask(t => ({ ...t, assigned_to: Number(selectedAssignee), assignee_name: name }));
      setSelectedAssignee('');
      setAssigneeSearch('');
      setAssignableUsers([]); // reset so next focus re-fetches with updated exclusion
      toast.success(isAdminRequest ? 'Request assigned successfully' : 'Task reassigned');
    } catch (err) {
      toast.error(err.response?.data?.error || (isAdminRequest ? 'Error assigning request' : 'Error reassigning task'));
    } finally { setAssignSaving(false); }
  };

  // ── Close task ────────────────────────────────────────────────────────────
  const handleCloseTask = async () => {
    if (!closureFeedback.trim()) { toast.error('Feedback is required to close this task'); return; }
    setClosing(true);
    try {
      await axios.put(`${apiUrl}/tasks/${taskId}`,
        { status: 'Closed', feedback: closureFeedback.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update all status-related fields so the UI reflects the closed state immediately
      setTask(t => ({ ...t, status: 'Closed', status_id: null, feedback: closureFeedback.trim() }));
      setShowCloseModal(false);
      setClosureFeedback('');
      toast.success('Task closed successfully');
    } catch (err) {
      const msg = err.response?.data?.error || 'Error closing task';
      toast.error(msg);
      // Keep modal open on error so user can retry
    } finally { setClosing(false); }
  };

  // ── Load reports removed — reports are email-only, not shown in UI ────────

  // ── Export Task ─────────────────────────────────────────────────────────────
  const handleExportTask = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const res = await fetch(`${apiUrl}/tasks/${taskId}/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Export failed (${res.status})`);
      }
      const bundle = await res.json();
      const wb = buildSpecificTaskWorkbook(bundle);
      const today = new Date().toISOString().split('T')[0];
      XLSX.writeFile(wb, `Task_${taskId}_Export_${today}.xlsx`);
      toast.success('Task exported successfully');
    } catch (err) {
      toast.error(err.message || 'Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const isSubtaskOnlyAccess = Boolean(task?._subtaskAccessOnly);
  const canAccess    = isManager
    || task?.assigned_to === currentUserId
    || Number(task?.created_by) === Number(currentUserId)   // ADMIN_REQUEST creator
    || isSubtaskOnlyAccess
    || isPeerAccess;
  const readOnly     = isTaskReadOnly(task);

  // Derive the task type code from the API response field
  const taskTypeCode = task?.task_type_code || task?.task_type || '';
  const isAdminRequest = taskTypeCode === 'ADMIN_REQUEST';
  const isAdminTask    = taskTypeCode === 'ADMIN_TASK';

  // ADMIN_TASK and ADMIN_REQUEST never show subtask section
  const subtasksAllowed = supportsSubtasks(task) && !isSubtaskOnlyAccess;

  // Comments:
  //   PROJECT       — shown to project members (existing behaviour)
  //   ADMIN_REQUEST — shown to creator + admins/superusers
  //   ADMIN_TASK    — shown (existing behaviour, assignee + admin)
  //   _subtaskAccessOnly — never (employee reached page via subtask only)
  const commentsAllowed = !isSubtaskOnlyAccess;

  // For ADMIN_REQUEST the employee is the creator — they can comment but NOT manage
  const isAdminRequestCreator = isAdminRequest &&
    Number(task?.created_by) === Number(currentUserId) &&
    role === 'employee';

  const createAllowed   = canCreateSubtask(currentUser, task) && subtasksAllowed;
  const editAllowed     = isManager || task?.assigned_to === currentUserId || isSubtaskOnlyAccess;
  const statusAllowed   = !isPeerAccess && canChangeStatus(currentUser, task);
  const closeAllowed    = !isPeerAccess && canCloseTask(currentUser, task);
  // Daily Report only applies to PROJECT tasks assigned to the employee
  const reportAllowed   = !isPeerAccess && !isManager && canSubmitReport(currentUser, task)
                          && !isAdminRequest && !isAdminTask;
  // Full management panel: manager/admin roles, except not on subtask-only access
  // Also exclude ADMIN_REQUEST from manageAllowed for non-admin employees
  const manageAllowed   = isManager && !isSubtaskOnlyAccess && !isAdminRequestCreator;
  const backPath = role === 'Manager'   ? '/Manager-dashboard'
                 : role === 'admin'     ? '/admin-dashboard'
                 : role === 'superuser' ? '/superuser-dashboard'
                 : '/dashboard';

  // When an admin navigates back from a task, restore the correct workflow tab.
  // ADMIN_REQUEST tasks live in "Management Requests"; ADMIN_TASK in "Org Tasks".
  const handleBack = () => {
    if (role === 'admin' || role === 'superuser') {
      if (isAdminRequest) {
        sessionStorage.setItem('admin_workflow_tab', 'Management Requests');
      } else if (isAdminTask) {
        sessionStorage.setItem('admin_workflow_tab', 'Org Tasks');
      }
    }
    navigate(backPath);
  };

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto flex flex-col items-center justify-center min-h-[60vh] gap-3">
        <div className="w-10 h-10 border-2 border-[#7AA33E] border-t-transparent rounded-full animate-spin" />
        <p className="text-gray-400 text-sm">Loading task details...</p>
      </div>
    );
  }

  if (!task || !canAccess) {
    return (
      <div className="p-6 max-w-7xl mx-auto flex flex-col items-center justify-center min-h-[60vh] gap-3">
        <svg className="w-12 h-12 text-red-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        </svg>
        <p className="text-red-500 font-semibold">Unauthorized or task not found.</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <Watermark />

      <button
        onClick={handleBack}
        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 mb-6 transition group"
      >
        <svg className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        Back to Dashboard
      </button>

      {/* Read-only banner */}
      {readOnly && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-gray-100 border border-gray-200 flex items-center gap-2 text-sm text-gray-500 font-medium">
          🔒 This task is <strong>Closed</strong> and is now read only. You can still view all information below.
        </div>
      )}

      {/* Peer-access info banner */}
      {isPeerAccess && !readOnly && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-blue-50 border border-blue-200 flex items-center gap-2 text-sm text-blue-600 font-medium">
          👥 You are viewing this as a project team member. You can view all details, subtasks, and participate in comments.
        </div>
      )}

      {/* Task header card */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-2">
        <div className="flex flex-col gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Task #{task.id}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${STATUS_BADGE[task.status] || 'bg-gray-100 text-gray-500'}`}>
                {(task.status || '').replace('_', ' ')}
              </span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${PRIORITY_BADGE[task.priority] || 'bg-gray-100 text-gray-500'}`}>
                {task.priority}
              </span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{task.title}</h1>
            <p className="text-gray-500 text-sm leading-relaxed">{task.description}</p>
          </div>
        </div>

        {/* Meta row */}
        <div className="mt-5 pt-5 border-t border-gray-100 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Assigned To</p>
            <p className="text-sm font-medium text-gray-800">{task.assignee_name || <span className="text-gray-400 italic">Unassigned</span>}</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Created By</p>
            <p className="text-sm font-medium text-gray-800">{task.created_by_name || '—'}</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Due Date</p>
            <p className="text-sm font-medium text-gray-800">
              {task.due_date ? new Date(task.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Created</p>
            <p className="text-sm font-medium text-gray-800">
              {task.created_at ? new Date(task.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
            </p>
          </div>
          {/* Project name — shown for PROJECT type tasks */}
          {task.task_type_code === 'PROJECT' && (
            <div className="sm:col-span-2">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Project</p>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#f0f7e6] border border-[#c6e29a] text-[#5a8a2a] text-sm font-semibold">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
                </svg>
                {task.project_name || `Project #${task.project_id}`}
              </span>
            </div>
          )}
        </div>

        {/* Closed feedback */}
        {readOnly && task.feedback && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Closure Feedback</p>
            <p className="text-sm text-gray-600 leading-relaxed">{task.feedback}</p>
          </div>
        )}
      </div>

      {/* ── Actions ── */}
      {(statusAllowed || reportAllowed) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-2">

          {/* Update Status card */}
          {statusAllowed && (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <div className="flex items-center gap-2 mb-1">
                <span className="w-1 h-5 rounded-full bg-[#7AA33E] inline-block" />
                <h2 className="text-sm font-bold text-gray-800">Update Status</h2>
              </div>
              <p className="text-xs text-gray-400 mb-5">Change the current status of this task</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'Open',     label: 'Open',        color: 'bg-blue-50 border-blue-200 text-blue-700',      active: 'bg-blue-500 border-blue-500 text-white' },
                  { value: 'Ongoing',  label: 'In Progress', color: 'bg-yellow-50 border-yellow-200 text-yellow-700', active: 'bg-yellow-400 border-yellow-400 text-white' },
                  { value: 'Blocked',  label: 'Blocked',     color: 'bg-red-50 border-red-200 text-red-700',          active: 'bg-red-500 border-red-500 text-white' },
                  { value: 'On Hold',  label: 'On Hold',     color: 'bg-orange-50 border-orange-200 text-orange-700', active: 'bg-orange-400 border-orange-400 text-white' },
                  { value: 'Closed',   label: 'Closed',      color: 'bg-gray-50 border-gray-200 text-gray-500',       active: 'bg-gray-600 border-gray-600 text-white' },
                ].map(({ value, label, color, active }) => {
                  const isClosedValue = value === 'Closed';
                  const isLocked = isClosedValue && !closeAllowed;

                  return (
                    <button
                      key={value}
                      type="button"
                      disabled={statusUpdating || isLocked}
                      onClick={() => !isLocked && handleUpdateStatus(value)}
                      title={
                        isLocked
                          ? (isAdminTask || isAdminRequest
                              ? 'Only admin can close this task'
                              : 'Only your manager can close this task')
                          : undefined
                      }
                      className={`px-4 py-2 rounded-xl border-2 text-xs font-bold transition flex items-center gap-1.5 ${
                        isLocked
                          ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed opacity-60 hover:opacity-60'
                          : task.status === value
                          ? active
                          : `${color} hover:opacity-80`
                      } disabled:opacity-50`}
                    >
                      {isLocked ? (
                        <>
                          <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          <span>{label}</span>
                        </>
                      ) : (
                        <>
                          {task.status === value && <span className="mr-1">✓</span>}
                          {label}
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
              {!closeAllowed && (
                <p className="text-xs text-gray-400 mt-3 flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <span>
                    Closed status is locked for employees. Main tasks can only be closed by your{' '}
                    {isAdminTask || isAdminRequest ? 'admin' : 'manager'}.
                  </span>
                </p>
              )}
              {statusUpdating && (
                <div className="flex items-center gap-2 mt-4 text-xs text-gray-400">
                  <div className="w-3.5 h-3.5 border-2 border-[#7AA33E] border-t-transparent rounded-full animate-spin" />
                  Saving...
                </div>
              )}
            </div>
          )}

          {/* Daily Report card */}
          {reportAllowed && (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <div className="flex items-center gap-2 mb-1">
                <span className="w-1 h-5 rounded-full bg-[#7AA33E] inline-block" />
                <h2 className="text-sm font-bold text-gray-800">Daily Report</h2>
              </div>
              <p className="text-xs text-gray-400 mb-5">Send a progress update to your manager</p>
              <form onSubmit={handleSendReport} className="flex flex-col gap-3">
                <div className="relative">
                  <textarea
                    value={reportNotes}
                    onChange={e => setReportNotes(e.target.value)}
                    placeholder="What did you work on today? Any blockers or next steps?"
                    rows={4}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:border-transparent focus:bg-white resize-none transition"
                    required
                  />
                  <span className="absolute bottom-3 right-3 text-xs text-gray-300">{reportNotes.length} chars</span>
                </div>
                {reportMsg && (
                  <div className={`flex items-center gap-2 px-4 py-3 rounded-xl text-xs font-semibold ${
                    reportMsg === 'success'
                      ? 'bg-green-50 text-green-700 border border-green-200'
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}>
                    <span>{reportMsg === 'success' ? '✓' : '!'}</span>
                    {reportMsg === 'success' ? 'Report sent to your manager.' : reportMsg}
                  </div>
                )}
                <div className="flex items-center justify-between pt-1">
                  <p className="text-xs text-gray-400">Reports are visible to your manager only</p>
                  <button
                    type="submit"
                    disabled={reportSending || !reportNotes.trim()}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#7AA33E] text-white font-semibold text-sm hover:bg-[#6a9335] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition shadow-sm"
                  >
                    {reportSending
                      ? <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sending...</>
                      : '↑ Send Report'
                    }
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}

      {/* ── Comments section ── */}
      {commentsAllowed && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Comments</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                {readOnly
                  ? 'This task is closed. The comment history is preserved and read-only.'
                  : isAdminRequest
                  ? 'Direct conversation between you and the admin handling this request.'
                  : isAdminTask
                  ? 'Comments on this organisation task.'
                  : 'Unified comments for this task and all its subtasks.'}
              </p>
            </div>
            <button
              onClick={() => setCommentsOpen(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#23283a] text-white text-sm font-semibold hover:bg-gray-800 transition"
            >
              💬 {readOnly ? 'View Comments' : 'Open Comments'}
              {unreadCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-red-600 text-white text-xs font-bold">
                  {unreadCount}
                </span>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ── Attachments section ── */}
      {commentsAllowed && (
        <AttachmentSection
          taskId={taskId}
          readOnly={readOnly}
          currentUserId={currentUserId}
          title="Attachments"
        />
      )}

      {/* ── Manager / Admin: Manage Task panel ── */}
      {manageAllowed && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-2">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-1 h-5 rounded-full bg-[#7AA33E] inline-block" />
            <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Manage Task</h2>
            {readOnly && <span className="ml-2 text-xs text-gray-400 font-normal">🔒 Task closed — read only</span>}
            <div className="ml-auto">
              <button
                onClick={handleExportTask}
                disabled={exporting}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-gray-200 bg-white text-gray-700 font-semibold text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                title="Export complete task data to Excel"
              >
                {exporting ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    Exporting…
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export Task
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* ── Edit Title & Description ── */}
            <div className="flex flex-col gap-3">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide">Edit Details</h3>
              {editMode ? (
                <div className="flex flex-col gap-2">
                  <input
                    value={editTitle}
                    onChange={e => setEditTitle(e.target.value)}
                    className="rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#7AA33E]"
                    placeholder="Task title"
                  />
                  <textarea
                    value={editDesc}
                    onChange={e => setEditDesc(e.target.value)}
                    rows={3}
                    className="rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] resize-none"
                    placeholder="Description"
                  />
                  <div className="flex gap-2">
                    <button onClick={handleSaveEdit} disabled={editSaving || !editTitle.trim()}
                      className="flex-1 py-2 rounded-xl bg-[#7AA33E] text-white text-xs font-semibold disabled:opacity-50 hover:bg-[#6a9335] transition">
                      {editSaving ? 'Saving…' : 'Save Changes'}
                    </button>
                    <button onClick={() => setEditMode(false)}
                      className="flex-1 py-2 rounded-xl bg-gray-100 text-gray-700 text-xs font-semibold hover:bg-gray-200 transition">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  disabled={readOnly}
                  onClick={() => { setEditTitle(task.title || ''); setEditDesc(task.description || ''); setEditMode(true); }}
                  className="self-start px-4 py-2 rounded-xl border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40 transition"
                >
                  ✎ Edit Title & Description
                </button>
              )}
            </div>

            {/* ── Priority & Due Date ── */}
            <div className="flex flex-col gap-3">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide">Priority & Due Date</h3>
              {!editPriority && !task.priority && (
                <p className="text-xs text-amber-600 font-medium">Select a priority before saving</p>
              )}
              <div className="flex gap-2 flex-wrap">
                {['High', 'Medium', 'Low'].map(p => (
                  <button key={p} disabled={readOnly}
                    onClick={() => setEditPriority(p)}
                    className={`px-3 py-1.5 rounded-xl border-2 text-xs font-bold transition disabled:opacity-40 ${
                      (editPriority || task.priority || 'Low') === p
                        ? p === 'High'   ? 'border-red-400 bg-red-50 text-red-700'
                          : p === 'Medium' ? 'border-yellow-400 bg-yellow-50 text-yellow-700'
                          : 'border-green-400 bg-green-50 text-green-700'
                        : 'border-gray-200 bg-white text-gray-500 hover:border-gray-400'
                    }`}>{p}</button>
                ))}
              </div>
              <input
                type="date" disabled={readOnly}
                value={editDueDate || (task.due_date ? task.due_date.split('T')[0] : '')}
                min={getMinDate()}
                onChange={e => {
                  const val = e.target.value;
                  const err = validateDueDate(val);
                  if (err) { toast.error(err); return; }
                  setEditDueDate(val);
                }}
                className="rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] disabled:opacity-40"
              />
              <button
                disabled={readOnly || editPrioritySaving}
                onClick={handleSavePriority}
                className="self-start px-4 py-2 rounded-xl bg-[#7AA33E] text-white text-xs font-semibold hover:bg-[#6a9335] disabled:opacity-40 transition"
              >
                {editPrioritySaving ? 'Saving…' : 'Save Priority & Date'}
              </button>
            </div>

            {/* ── Assign / Reassign ── */}
            <div className="flex flex-col gap-3">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                {isAdminRequest ? 'Assign Request' : 'Reassign Task'}
              </h3>
              <p className="text-xs text-gray-400">
                {isAdminRequest
                  ? <>Request submitted by: <strong className="text-gray-700">{task.created_by_name || 'Employee'}</strong> · Currently assigned to: <strong className="text-gray-700">{task.assignee_name || 'Unassigned'}</strong></>
                  : <>Currently assigned to: <strong className="text-gray-700">{task.assignee_name || 'Unassigned'}</strong></>
                }
              </p>
              <div className="flex flex-col gap-2">
                {/* Search input — loads list on first focus */}
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search by name or employee ID..."
                    value={assigneeSearch}
                    disabled={readOnly}
                    onChange={e => setAssigneeSearch(e.target.value)}
                    onFocus={loadAssignableUsers}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 pl-9 pr-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white focus:border-transparent disabled:opacity-40 transition"
                  />
                </div>
                {/* Filtered user list */}
                {assignableUsers.length > 0 && (
                  <div className="border border-gray-200 rounded-xl overflow-hidden max-h-44 overflow-y-auto divide-y divide-gray-100">
                    {(() => {
                      const q = assigneeSearch.trim().toLowerCase();
                      const filtered = q
                        ? assignableUsers.filter(u =>
                            `${u.first_name} ${u.last_name}`.toLowerCase().includes(q) ||
                            (u.employee_id || '').toLowerCase().includes(q)
                          )
                        : assignableUsers;
                      if (filtered.length === 0)
                        return (
                          <div className="flex flex-col items-center py-5 text-gray-400">
                            <svg className="w-7 h-7 mb-1.5 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-1a4 4 0 00-4-4H4a4 4 0 00-4 4v1h5m6-10a4 4 0 100-8 4 4 0 000 8z" /></svg>
                            <p className="text-xs">No users match your search</p>
                          </div>
                        );
                      const roleBadge = (u) => {
                        const r = (u.role || u.role_name || '').toLowerCase();
                        if (r === 'admin') return 'bg-blue-50 text-blue-600';
                        if (r === 'manager') return 'bg-yellow-50 text-yellow-600';
                        return 'bg-green-50 text-green-600';
                      };
                      return filtered.map(u => {
                        const selected = String(selectedAssignee) === String(u.id);
                        return (
                          <button
                            key={u.id}
                            type="button"
                            onClick={() => setSelectedAssignee(String(u.id))}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition ${selected ? 'bg-[#f4f9ec]' : 'bg-white hover:bg-gray-50'}`}
                          >
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0 ${selected ? 'bg-[#7AA33E] text-white' : 'bg-green-100 text-green-700'}`}>
                              {(u.first_name?.[0] || '').toUpperCase()}{(u.last_name?.[0] || '').toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-800 truncate">{u.first_name} {u.last_name}</p>
                              {u.employee_id && <p className="text-xs text-gray-400">{u.employee_id}</p>}
                            </div>
                            {u.role || u.role_name ? (
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${roleBadge(u)}`}>
                                {(u.role || u.role_name).charAt(0).toUpperCase() + (u.role || u.role_name).slice(1).toLowerCase()}
                              </span>
                            ) : null}
                            {selected && (
                              <svg className="w-4 h-4 text-[#7AA33E] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            )}
                          </button>
                        );
                      });
                    })()}
                  </div>
                )}
                <button
                  disabled={readOnly || !selectedAssignee || assignSaving}
                  onClick={handleAssign}
                  className="self-start flex items-center gap-2 px-4 py-2 rounded-xl bg-[#7AA33E] text-white text-xs font-semibold hover:bg-[#6a9335] disabled:opacity-40 transition"
                >
                  {assignSaving ? (
                    <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving…</>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      {isAdminRequest ? 'Assign' : 'Reassign'}
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* ── Close Task ── */}
            <div className="flex flex-col gap-3">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide">Close Task</h3>
              {readOnly ? (
                <p className="text-xs text-gray-400">This task is already closed.</p>
              ) : !closeAllowed ? (
                <p className="text-xs text-gray-400">
                  🔒 This task can only be closed by {isAdminTask || isAdminRequest ? 'an admin' : 'the project manager'}.
                </p>
              ) : (
                <>
                  <p className="text-xs text-gray-400">Closing requires feedback. All subtasks must be completed first.</p>
                  <button
                    onClick={() => { setShowCloseModal(true); setClosureFeedback(''); }}
                    className="self-start px-4 py-2 rounded-xl bg-red-600 text-white text-xs font-semibold hover:bg-red-700 transition"
                  >
                    🔒 Close Task
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Close Task Modal ── */}
      {showCloseModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-gray-100">
            <div className="flex items-center gap-3 px-7 py-5 border-b border-gray-100">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 leading-tight">Close Task #{task.id}</h3>
                <p className="text-xs text-gray-400 mt-0.5">Provide closure feedback. This cannot be undone.</p>
              </div>
            </div>
            <div className="px-7 py-5 flex flex-col gap-4">
              <textarea
                className="w-full rounded-xl border border-gray-200 bg-gray-50 text-gray-800 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent focus:bg-white resize-none text-sm transition"
                rows={4}
                placeholder="Enter feedback / summary (required)"
                value={closureFeedback}
                onChange={e => setClosureFeedback(e.target.value)}
              />
            </div>
            <div className="flex gap-3 px-7 py-5 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
              <button
                className="flex-1 py-2.5 rounded-xl bg-white border border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-100 transition"
                onClick={() => setShowCloseModal(false)}
              >Cancel</button>
              <button
                disabled={!closureFeedback.trim() || closing}
                onClick={handleCloseTask}
                className="flex-1 py-2.5 rounded-xl bg-red-600 text-white font-semibold text-sm hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
              >
                {closing ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Closing…</>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Close Task
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subtasks section — only for PROJECT tasks */}
      {subtasksAllowed && (
        <SubtaskList
          taskId={task.id}
          canCreate={createAllowed}
          canEdit={editAllowed}
          parentTask={task}
          currentUser={currentUser}
          onViewParentTask={null}
        />
      )}

      {/* Comments panel */}
      {commentsAllowed && (
        <CommentsPanel
          open={commentsOpen}
          onClose={() => setCommentsOpen(false)}
          taskId={taskId}
          task={task}
          currentUserId={currentUserId}
          token={token}
          unreadCount={unreadCount}
          onUnreadClear={() => setUnreadCount(0)}
        />
      )}
    </div>
  );
}
